import * as React from 'react';
import { useTheme } from '@mui/material';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button, { ButtonProps } from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import * as Device from 'react-device-detect';
import { upperFirst } from 'lodash';

import { useOpenTicketAuthMutation, useListSupportTicketTopicsQuery, SupportTicketTopicEnum } from '@graphql';
import { useSession, useRecaptcha } from '@hooks';
import { parseApolloError } from '@utils';

interface ContactSupportProps extends ButtonProps {
  display: boolean;
  siret?: string;
}

export function ContactSupport(props: Readonly<ContactSupportProps>) {
  const { display, siret, variant = 'outlined' } = props;
  const { user, account } = useSession();
  const [open, setOpen] = React.useState(false);
  const [ticketId, setTicketId] = React.useState<string | null>(null);
  const [ip, setIp] = React.useState<string | null>(null);
  const { t, i18n } = useTranslation('cta');
  const { executeRecaptcha, isRecaptchaReady } = useRecaptcha();
  const navigate = useNavigate();
  const theme = useTheme();

  const { data: topicsData } = useListSupportTicketTopicsQuery({
    variables: { locale: i18n.language },
  });

  const [openTicketAuth, { data, loading, error }] = useOpenTicketAuthMutation();

  const isCalled = !!data?.openTicketAuth || !!error;

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleBack = () => {
    setOpen(false);
  };

  const handleClose = () => {
    navigate('/', { replace: true });
    setOpen(false);
  };

  // Fetch IP when dialog is opened
  React.useEffect(() => {
    if (!open) return;

    const controller = new AbortController();

    (async () => {
      try {
        const res = await fetch('/api/viewer-context', {
          credentials: 'include',
          signal: controller.signal,
        });
        const data = (await res.json()) as { ip?: string };
        setIp(data.ip ?? null);
      } catch {
        setIp(null);
      }
    })();

    return () => {
      controller.abort();
    };
  }, [open]);

  const formik = useFormik({
    validateOnMount: true,
    initialValues: {
      message: '',
    },
    validationSchema: yup.object({
      message: yup.string().min(10).required(),
    }),
    onSubmit: async (values) => {
      if (!isRecaptchaReady) {
        // reCAPTCHA script not ready yet
        import.meta.env.DEV && console.error('reCAPTCHA not ready');
        return;
      }

      if (!topicsData?.listSupportTicketTopics) {
        // reCAPTCHA script not ready yet
        import.meta.env.DEV && console.error('Support ticket topics not loaded');
        return;
      }

      const accurateTopic = topicsData.listSupportTicketTopics.find(
        (t) => t.topic === SupportTicketTopicEnum.SiretIssue
      );

      if (!accurateTopic) {
        import.meta.env.DEV && console.error('No accurate topic found for SIRET issue');
        return;
      }

      const recaptchaToken = await executeRecaptcha('contact_support');

      if (!recaptchaToken) {
        import.meta.env.DEV && console.error('Failed to get reCAPTCHA token');
        return;
      }

      const { data, errors } = await openTicketAuth({
        variables: {
          input: {
            topic: SupportTicketTopicEnum.SiretIssue,
            message: values.message,
            recaptchaToken: recaptchaToken,
          },
        },
      });

      if (data?.openTicketAuth?.id) {
        setTicketId(data.openTicketAuth.id);
      }

      if (errors && errors.length > 0) {
        const parsedError = parseApolloError(errors[0]);
        import.meta.env.DEV && console.error('Error while opening support ticket:', parsedError);
        return;
      }
    },
  });

  if (!account || !user) {
    return null;
  }

  return (
    <>
      {display && (
        <Button variant={variant} color="uncolored" onClick={handleClickOpen}>
          {t('contact support')}
        </Button>
      )}
      <Dialog open={open} onClose={handleBack} fullScreen={Device.isMobile}>
        <DialogTitle>{upperFirst(t('contact support'))}</DialogTitle>
        <DialogContent
          style={
            isCalled
              ? {
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  justifyContent: 'center',
                }
              : undefined
          }
        >
          {isCalled ? (
            <Stack height="100%" spacing={3}>
              <Alert severity={error ? 'error' : 'success'} sx={{ mb: 3 }}>
                {!error && <AlertTitle>{t('alert.title', { ns: 'support' })}</AlertTitle>}
                {t(`alert.${error ? 'error' : 'success'}`, { ns: 'support' })}
              </Alert>

              {data?.openTicketAuth.id && (
                <Stack
                  spacing={1}
                  my={2}
                  divider={<Divider />}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 2,
                    backgroundColor: 'background.paper',
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ wordBreak: 'break-all', cursor: 'default' }}
                  >
                    {t(`Ticket ID`, { ns: 'support' })}
                  </Typography>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ wordBreak: 'break-all', cursor: 'default' }}>
                      {data?.openTicketAuth.id ?? ticketId}
                    </Typography>
                    {!error && (
                      <Button
                        variant="contained"
                        color="uncolored"
                        size="small"
                        startIcon={<theme.icons.copy />}
                      >
                        {t('copy')}
                      </Button>
                    )}
                  </Stack>
                </Stack>
              )}

              <Button variant="contained" color="uncolored" onClick={handleClose}>
                {t('close')}
              </Button>
            </Stack>
          ) : (
            <>
              <DialogContentText>
                {t(
                  `contact.registerSpot.For any questions or issues regarding your establishment's registration, please fill out the form below. Our support team will respond as soon as possible.`,
                  { ns: 'support' }
                )}
              </DialogContentText>
              <Divider sx={{ my: 3 }} />
              <form onSubmit={formik.handleSubmit} id="register-contact-support-form">
                <Stack spacing={3}>
                  {siret?.length === 14 && (
                    <TextField
                      disabled
                      size="small"
                      margin="dense"
                      id="siret"
                      name="siret"
                      label="siret"
                      type="text"
                      fullWidth
                      variant="outlined"
                      value={siret}
                    />
                  )}
                  {ip && (
                    <TextField
                      disabled
                      size="small"
                      margin="dense"
                      id="ip"
                      name="ip"
                      label="ip"
                      type="text"
                      fullWidth
                      variant="outlined"
                      value={ip}
                    />
                  )}
                  <TextField
                    disabled
                    margin="dense"
                    size="small"
                    id="firstname"
                    name="firstname"
                    label={t('firstname.label', { ns: 'field' })}
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={user.firstname}
                  />
                  <Divider sx={{ my: 3 }} />
                  <TextField
                    required
                    margin="dense"
                    id="message"
                    name="message"
                    label={t('message.label', { ns: 'field' })}
                    type="text"
                    fullWidth
                    variant="outlined"
                    multiline
                    minRows={15}
                    maxRows={20}
                    value={formik.values.message}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.message && Boolean(formik.errors.message)}
                    helperText={formik.touched.message && formik.errors.message}
                  />
                </Stack>
              </form>
            </>
          )}
        </DialogContent>
        {!isCalled && (
          <DialogActions>
            <Button variant="outlined" color="uncolored" onClick={handleBack} disabled={loading}>
              {t('cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              form="register-contact-support-form"
              disabled={!formik.isValid || formik.isSubmitting}
              loading={loading}
            >
              {t('send')}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
}
